import { makeAutoObservable, runInAction } from "mobx";
import { getOtherDirectorsPersonalCodes } from "helpers/forms/director";
import type { AnalyticsManager } from "services/analytics/analyticsManager";
import type { ComplianceClient } from "services/compliance/complianceClient";
import type { DirectorStore } from "services/director/directorStore";
import { DIRECTOR_CH_VERIFICATION_STATUS, type DirectorData } from "services/director/directorTypes";
import {
  TeamMemberChVerificationState,
  getTeamMemberChVerificationState,
} from "services/director/teamMemberChVerificationState";
import { DirectorPosition, type DirectorPositionStore } from "services/directorPosition/directorPositionStore";
import type { FeatureStore } from "services/feature/featureStore";
import { DirectorVerificationStatusData } from "./directorCodeModalTypes";

/** `unavailable`: no result came within the polling limit, so the address check explains nothing either way */
type ComplianceState = "idle" | "loading" | "failed" | "success" | "unavailable";

export type DuplicatedIdentityPhase = "options" | "confirmRemoval" | "linkSent";

const COMPLIANCE_NOT_FOUND_CODE = 1040;
const COMPLIANCE_POLL_INTERVAL_MS = 3000;
// About a minute, so a result that never comes can't keep the modal on its loader
const COMPLIANCE_MAX_POLL_ATTEMPTS = 20;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isComplianceNotReady = (e: any): boolean => e?.data?.error?.code === COMPLIANCE_NOT_FOUND_CODE;

export class DirectorCodeModalStore {
  private _requesting = false;
  private _submitting = false;
  private _code = "";
  private _verificationStatus: DirectorVerificationStatusData | null = null;
  private _error: string | null = null;
  private _pollingInterval: ReturnType<typeof setInterval> | null = null;
  private _compliancePollingInterval: ReturnType<typeof setInterval> | null = null;
  private _isInitializing = false;
  private _mainDirectorRequesting = false;
  private _removing = false;
  private _complianceState: ComplianceState = "idle";
  private _duplicatedIdentityPhase: DuplicatedIdentityPhase = "options";

  constructor(
    private readonly _directorStore: DirectorStore,
    private readonly _directorPositionStore: DirectorPositionStore,
    private readonly _analyticsManager: AnalyticsManager,
    private readonly _featureStore: FeatureStore,
    private readonly _complianceClient: ComplianceClient,
    private readonly _directorId?: string,
  ) {
    makeAutoObservable(this);
  }

  get director(): DirectorData | undefined {
    return this._directorId ? this._directorStore.tryGetDirectorById(this._directorId) : undefined;
  }

  get title(): string {
    return this._directorId
      ? this._directorPositionStore.getForDirector(this._directorId) || DirectorPosition.DirectorAndShareholder
      : DirectorPosition.DirectorAndShareholder;
  }

  get isRequesting(): boolean {
    return this._requesting;
  }

  get isSubmitting(): boolean {
    return this._submitting;
  }

  get code(): string {
    return this._code;
  }

  set code(value: string) {
    this._code = value;
  }

  get error(): string | null {
    return this._error;
  }

  get verificationState(): TeamMemberChVerificationState {
    return getTeamMemberChVerificationState(this.director, this._verificationStatus ?? undefined);
  }

  get isWaitingForVerification(): boolean {
    return this.verificationState === TeamMemberChVerificationState.IdPending;
  }

  /** Their ID check is done and the ACSP form has been registered, whatever came of it */
  get isVerified(): boolean {
    return [
      TeamMemberChVerificationState.CodeRequested,
      TeamMemberChVerificationState.CodeSent,
      TeamMemberChVerificationState.WrongEmail,
      TeamMemberChVerificationState.AcspDuplicateIdentity,
      TeamMemberChVerificationState.ApplePrivateRelayEmail,
      TeamMemberChVerificationState.Failed,
    ].includes(this.verificationState);
  }

  get hasCode(): boolean {
    return Boolean(this.director?.chPersonalCode);
  }

  get otherDirectorsPersonalCodes(): string[] {
    return getOtherDirectorsPersonalCodes(this._directorStore.directors, this._directorId);
  }

  get isInitializing(): boolean {
    return this._isInitializing;
  }

  get isMainDirectorRequesting(): boolean {
    return this._mainDirectorRequesting;
  }

  get mainDirectorIdentityVerificationStatus(): DIRECTOR_CH_VERIFICATION_STATUS | undefined {
    const mainDirector = this._directorStore.mainDirector;
    return mainDirector?.identityVerificationStatus;
  }

  get isAcspIdvSubmissionEnabled(): boolean {
    return this._featureStore.hasFeature("formationAcspIdvSubmissionEnabled");
  }

  get isMainDirector(): boolean {
    return Boolean(this._directorId) && this._directorId === this._directorStore.mainDirector?.id;
  }

  /** Someone else already passed the ID check under this person's identity, so the application holds a duplicate */
  get isDuplicatedIdentity(): boolean {
    return this.verificationState === TeamMemberChVerificationState.DuplicatedIdentity;
  }

  /**
   * Owned here rather than as local component state: resending clears the backend status that
   * isDuplicatedIdentity depends on, which would otherwise unmount the step and lose a local phase mid-confirmation.
   */
  get duplicatedIdentityPhase(): DuplicatedIdentityPhase {
    return this._duplicatedIdentityPhase;
  }

  setDuplicatedIdentityPhase(phase: DuplicatedIdentityPhase): void {
    this._duplicatedIdentityPhase = phase;
  }

  get isRemoving(): boolean {
    return this._removing;
  }

  get isTruliooComplianceFailed(): boolean {
    return this._complianceState === "failed";
  }

  get complianceState(): ComplianceState {
    return this._complianceState;
  }

  async init(): Promise<void> {
    if (!this._directorId) {
      return;
    }

    runInAction(() => {
      this._duplicatedIdentityPhase = "options";
    });

    if (this.isMainDirector) {
      this._analyticsManager.event("ch_verification_main_director_init_skipped", {
        directorId: this._directorId,
      });
      return;
    }

    runInAction(() => {
      this._isInitializing = true;
    });

    try {
      // The status and fail reason are read off the director, which may have moved on since the list loaded
      await this._directorStore.refreshDirectors();
      // Get verification status from GET endpoint (secondary directors only)
      const status = await this._directorStore.getSecondaryDirectorVerificationStatus(this._directorId);
      runInAction(() => {
        this._verificationStatus = status ?? null;
      });

      if (this.isWaitingForVerification) {
        await this._runComplianceCheck(this._directorId);
      }
    } catch (error) {
      console.error("Error checking IDV status:", error);
      this.setError(error as Error);
    } finally {
      runInAction(() => {
        this._isInitializing = false;
      });
    }
  }

  async requestVerification(): Promise<void> {
    if (!this._directorId) {
      return;
    }

    this._requesting = true;
    this.clearError();

    try {
      if (!this.isAcspIdvSubmissionEnabled) {
        this._analyticsManager.event("ch_verification_secondary_director_self_service", {
          directorId: this._directorId,
        });
        return;
      }

      // POST to request verification
      await this._directorStore.requestDirectorVerification(this._directorId);

      // Immediately set status to "waiting" (IDV started, ACSP not submitted)
      runInAction(() => {
        this._verificationStatus = new DirectorVerificationStatusData({
          isIdvStarted: true,
          isAcspFormSubmitted: false,
        });
      });

      this._analyticsManager.event("ch_verification_secondary_director_requested", {
        directorId: this._directorId,
      });

      await this._runComplianceCheck(this._directorId);
    } catch (error) {
      console.error("Error requesting verification:", error);
      this.setError(error as Error);
    } finally {
      runInAction(() => {
        this._requesting = false;
      });
    }
  }

  async requestMainDirectorVerification(): Promise<void> {
    if (!this._directorId) {
      return;
    }
    runInAction(() => {
      this._mainDirectorRequesting = true;
      this.clearError();
    });

    try {
      await this._directorStore.submitMainDirectorACSPVerification();
      this._analyticsManager.event("ch_verification_main_director_requested", {
        directorId: this._directorId,
      });

      this.startMainDirectorPolling();
    } catch (error) {
      console.error("Error requesting main director verification:", error);
      this.setError(error as Error);
      runInAction(() => {
        this._mainDirectorRequesting = false;
      });
    }
  }

  /** Sends a fresh IDV link so the director can verify again with their own documents */
  async resendIdentityVerification(): Promise<void> {
    if (!this._directorId) {
      return;
    }

    runInAction(() => {
      this._requesting = true;
      this.clearError();
    });

    try {
      await this._directorStore.resetDirectorVerification(this._directorId);
      this._analyticsManager.event("ch_verification_duplicated_identity_link_resent", {
        directorId: this._directorId,
      });
    } catch (error) {
      console.error("Error resending identity verification:", error);
      this.setError(error as Error);
      throw error;
    } finally {
      runInAction(() => {
        this._requesting = false;
      });
    }
  }

  /** Drops the duplicated person from the application entirely */
  async removeDirector(): Promise<void> {
    if (!this._directorId) {
      return;
    }

    runInAction(() => {
      this._removing = true;
      this.clearError();
    });

    try {
      await this._directorStore.deleteDirector(this._directorId);
      this._analyticsManager.event("ch_verification_duplicated_identity_director_removed", {
        directorId: this._directorId,
      });
    } catch (error) {
      console.error("Error removing director:", error);
      this.setError(error as Error);
      throw error;
    } finally {
      runInAction(() => {
        this._removing = false;
      });
    }
  }

  async confirmCode(code: string): Promise<void> {
    if (!this._directorId || !code) {
      return;
    }

    this._submitting = true;
    this.clearError();

    try {
      await this._directorStore.updateCHPersonalCode(this._directorId, code);
      this._analyticsManager.event("ch_verification_personal_code_entered", {
        directorId: this._directorId,
      });

      // Refresh director list to update UI
      await this._directorStore.init();
    } catch (error) {
      console.error("Error updating code:", error);
      this.setError(error as Error);
      throw error;
    } finally {
      runInAction(() => {
        this._submitting = false;
      });
    }
  }

  startSecondaryDirectorPolling(): void {
    if (this._pollingInterval || !this._directorId) {
      return;
    }

    this._pollingInterval = setInterval(async () => {
      if (!this._directorId) {
        return;
      }

      try {
        const status = await this._directorStore.getSecondaryDirectorVerificationStatus(this._directorId);

        runInAction(() => {
          this._verificationStatus = status || null;
        });

        this._analyticsManager.event("ch_verification_secondary_director_status_checked", {
          directorId: this._directorId,
          status: status?.isAcspFormSubmitted ? "VERIFIED" : "SUBMITTED",
        });

        if (this.isVerified) {
          this.stopPolling();
        }
      } catch (error) {
        console.error("Error polling secondary director status:", error);
        this.stopPolling();
        this.setError(error as Error);
      }
    }, 5000);
  }

  startMainDirectorPolling(): void {
    if (this._pollingInterval || !this._directorId) {
      return;
    }

    const directorId = this._directorId;

    this._pollingInterval = setInterval(async () => {
      try {
        const status = await this._directorStore.getDirectorCHVerificationStatus();

        this._analyticsManager.event("ch_verification_main_director_status_checked", {
          directorId,
          status: status ?? "PENDING",
        });

        if (status !== undefined) {
          this.stopPolling();
          runInAction(() => {
            this._mainDirectorRequesting = false;
          });
        }
      } catch (error) {
        console.error("Error polling main director status:", error);
        this.stopPolling();
        this.setError(error as Error);
        runInAction(() => {
          this._mainDirectorRequesting = false;
        });
      }
    }, 5000);
  }

  stopPolling(): void {
    if (this._pollingInterval) {
      clearInterval(this._pollingInterval);
      this._pollingInterval = null;
    }
  }

  dispose(): void {
    this.stopPolling();
    this._stopCompliancePolling();
  }

  private async _runComplianceCheck(directorId: string): Promise<void> {
    runInAction(() => {
      this._complianceState = "loading";
    });

    try {
      const result = await this._complianceClient.getComplianceResult(directorId);

      if (!result.isFinished) {
        // Poll until finished
        await this._pollComplianceResult(directorId);
        return;
      }

      runInAction(() => {
        this._complianceState = result.isSuccessful ? "success" : "failed";
      });
    } catch (e) {
      if (isComplianceNotReady(e)) {
        await this._pollComplianceResult(directorId);
        return;
      }
      runInAction(() => {
        this._complianceState = "failed";
      });
    }
  }

  private _stopCompliancePolling(): void {
    if (this._compliancePollingInterval) {
      clearInterval(this._compliancePollingInterval);
      this._compliancePollingInterval = null;
    }
  }

  private async _pollComplianceResult(directorId: string): Promise<void> {
    let attempts = 0;
    let isSettled = false;
    await new Promise<void>((resolve) => {
      // Requests can overlap, so the first answer settles the check and any later one is ignored:
      // a response landing after the attempts ran out must not switch the modal mid-entry
      const settle = (state: ComplianceState): void => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        this._stopCompliancePolling();
        runInAction(() => {
          this._complianceState = state;
        });
        resolve();
      };

      this._compliancePollingInterval = setInterval(async () => {
        attempts += 1;
        if (attempts > COMPLIANCE_MAX_POLL_ATTEMPTS) {
          settle("unavailable");
          return;
        }
        try {
          const result = await this._complianceClient.getComplianceResult(directorId);
          if (result.isFinished) {
            settle(result.isSuccessful ? "success" : "failed");
          }
        } catch (e) {
          // 1040 = not ready yet, continue polling
          if (!isComplianceNotReady(e)) {
            settle("failed");
          }
        }
      }, COMPLIANCE_POLL_INTERVAL_MS);
    });
  }

  private setError(error: Error): void {
    runInAction(() => {
      this._error = error.message;
    });
  }

  private clearError(): void {
    runInAction(() => {
      this._error = null;
    });
  }
}
